import {
  PACE_DISCLAIMER,
  PACE_GLYPHS,
  PACE_KINDS,
  type PaceKind,
} from '../../shared/alerts/pace';
import { BRAND_NAME } from '../../shared/brand';

/**
 * The landing page's Momentum section, and the Momentum alert type in "What
 * an alert looks like": three raises, one per change of pace, as the
 * "Momentum Marketing" brief (28 Oct 2026) gives them. Pure, so the server
 * render (view.ts), the browser (client/momentum.ts) and the tests agree.
 *
 * ILLUSTRATIVE: the alarm is not built, no raise is measured, and none of
 * these figures is a claim about a real one. They are consistent with each
 * other, though, and should stay so: Morrow Foods' €43,520 + €330 × 16 days
 * = €48,800, 61% of €80,000; Tessel Labs' $335,700 left at $84k a day is 4
 * days; Halden Cycles' £28,800 left at £22.3k a day is 31 hours.
 */

export interface MomentumEmail {
  /** "Wed 28 Oct, 09:12": when the alarm went. */
  time: string;
  subject: string;
  kicker: string;
  finding: string;
  cells: { label: string; value: string }[];
  platform: string;
  /** The alert section's deadline row: "○ Closes Fri 6 Nov 2026 · 23:59 ET". */
  closes: string;
  /** "9 days". */
  left: string;
  /** The alert section's "Why you got this", before its links. */
  why: string;
}

export interface MomentumScenario {
  key: PaceKind;
  /** The picker's card. */
  title: string;
  description: string;
  name: string;
  meta: string;
  /** The pace chip: "↗ 3.2× usual pace". */
  chip: string;
  /** Raised per day over the last 14 days, in thousands; the last is today. */
  bars: number[];
  /** Usual pace, in the same thousands: the median of the previous 7 days. */
  usual: number;
  usualLabel: string;
  /** What today's bar took in: "+$84k". */
  today: string;
  /** The callout over today's bar, two lines. */
  callout: [string, string];
  progress: {
    /** "PROGRESS TO THE $1.24M MAX". */
    label: string;
    /** Raised, in percent of the goal. */
    solid: number;
    /** What this pace adds by the time it is measured to, in percent. */
    projW: number;
    raised: string;
    atPace: string;
    goalLabel: string;
    goal: string;
  };
  provenance: string;
  /** What a screen reader hears in place of the chart. */
  summary: string;
  email: MomentumEmail;
}

/** Every alarm's sender, as every other alert's. */
export const MOMENTUM_FROM = `${BRAND_NAME} Alerts`;

/** The first of the chart's 14 days; the last is today, Wed 28 Oct. */
export const AXIS_START = 'Thu 15 Oct';

const WHY_END = `Manage alerts · Unsubscribe. ${PACE_DISCLAIMER}`;

export const MOMENTUM: Record<PaceKind, MomentumScenario> = {
  up: {
    key: 'up',
    title: 'Speeding up',
    description: 'Last 24 hours at 2× its usual pace or more.',
    name: 'Tessel Labs',
    meta: 'Wefunder · Reg CF · US · SaaS',
    chip: '↗ 3.2× usual pace',
    bars: [21, 24, 19, 27, 30, 22, 25, 26, 23, 29, 26, 24, 28, 84],
    usual: 26,
    usualLabel: '$26k/day',
    today: '+$84k',
    callout: ['↗ 3.2× usual pace', 'alarm sent 09:12'],
    progress: {
      label: 'PROGRESS TO THE $1.24M MAX',
      solid: 73,
      projW: 27,
      raised: '$904,300',
      atPace: 'max in ~4 days',
      goalLabel: 'Max · closes in 9 days',
      goal: '$1.24M',
    },
    provenance: '↳ Wefunder · checked 6 min ago · 212 checks since 12 Oct',
    summary:
      'Tessel Labs, last 24 hours $84,000, 3.2 times its usual pace of $26,000 a day. At this pace it reaches its $1.24M max in about 4 days.',
    email: {
      time: 'Wed 28 Oct, 09:12',
      subject: '↗ Tessel Labs is filling 3.2× faster than usual',
      kicker: '↗ SPEEDING UP · 3.2× USUAL PACE',
      finding:
        'Tessel Labs took in $84,000 in the last 24 hours, 3.2× its usual pace. Wefunder · Reg CF · US · SaaS.',
      cells: [
        { label: 'Last 24h', value: '+$84,000 · +61 inv.' },
        { label: 'Usual pace', value: '$26,000/day' },
        { label: 'Raised', value: '$904,300 · 73% of max' },
        { label: 'At this pace', value: 'Max in ~4 days' },
      ],
      platform: 'Wefunder',
      closes: '○ Closes Fri 6 Nov 2026 · 23:59 ET',
      left: '9 days',
      why: 'Why you got this: Tessel Labs is on your watchlist and you asked for momentum alarms. Investors can cancel until 48 hours before the deadline, so totals can fall.',
    },
  },
  sight: {
    key: 'sight',
    title: 'Goal in sight',
    description: 'At this pace, target within 48 hours or max within 72.',
    name: 'Halden Cycles',
    meta: 'Crowdcube · FCA · GB · Mobility',
    chip: '◎ Target in ~31 h',
    bars: [
      6.1, 7.8, 8.4, 9.0, 10.2, 8.8, 9.9, 9.4, 8.7, 10.1, 9.6, 8.9, 9.4, 22.3,
    ],
    usual: 9.4,
    usualLabel: '£9.4k/day',
    today: '+£22.3k',
    callout: ['◎ target in ~31 h', 'alarm sent 11:40'],
    progress: {
      label: 'PROGRESS TO THE £180,000 TARGET',
      solid: 84,
      projW: 16,
      raised: '£151,200',
      atPace: 'target in ~31 h',
      goalLabel: 'Target · closes in 18 days',
      goal: '£180,000',
    },
    provenance: '↳ Crowdcube · checked 4 min ago · 188 checks since 9 Oct',
    summary:
      'Halden Cycles, last 24 hours £22,300, 2.4 times its usual pace of £9,400 a day. At this pace it reaches its £180,000 target in about 31 hours.',
    email: {
      time: 'Wed 28 Oct, 11:40',
      subject: '◎ Halden Cycles: target in about 31 hours at this pace',
      kicker: '◎ GOAL IN SIGHT · TARGET IN ~31 HOURS',
      finding:
        'At this pace, Halden Cycles reaches its £180,000 target in about 31 hours. Crowdcube · FCA · GB · Mobility.',
      cells: [
        { label: 'Last 24h', value: '+£22,300 · +38 inv.' },
        { label: 'Usual pace', value: '£9,400/day' },
        { label: 'Raised', value: '£151,200 · 84%' },
        { label: 'At this pace', value: 'Target in ~31 h' },
      ],
      platform: 'Crowdcube',
      closes: '○ Closes Sun 15 Nov 2026 · 23:59 GMT',
      left: '18 days',
      why: 'Why you got this: Halden Cycles is on your watchlist and you asked for momentum alarms.',
    },
  },
  down: {
    key: 'down',
    title: 'Slowing down',
    description: 'At this pace, it misses its target by the deadline.',
    name: 'Morrow Foods',
    meta: 'Companisto · ECSPR · DE · Food',
    chip: '↘ 0.3× usual pace',
    bars: [
      2.4, 1.9, 1.6, 1.4, 1.2, 1.3, 1.0, 1.1, 1.2, 0.9, 1.1, 1.3, 1.0, 0.33,
    ],
    usual: 1.1,
    usualLabel: '€1.1k/day',
    today: '+€330',
    callout: ['↘ 0.3× usual pace', 'alarm sent 08:05'],
    progress: {
      label: 'PROGRESS TO THE €80,000 TARGET',
      solid: 54,
      projW: 7,
      raised: '€43,520',
      atPace: '61% by Fri 13 Nov',
      goalLabel: 'Target · closes in 16 days',
      goal: '€80,000',
    },
    provenance: '↳ Companisto · checked 9 min ago · 164 checks since 14 Oct',
    summary:
      'Morrow Foods, last 24 hours €330, 0.3 times its usual pace of €1,100 a day. At this pace it reaches 61% of its €80,000 target by Fri 13 Nov.',
    email: {
      time: 'Wed 28 Oct, 08:05',
      subject: '↘ Morrow Foods has slowed to 0.3× its usual pace',
      kicker: '↘ SLOWING DOWN · 0.3× USUAL PACE',
      finding:
        'Morrow Foods is filling at a third of its usual pace. At this pace it reaches 61% of its target by Fri 13 Nov. Companisto · ECSPR · DE.',
      cells: [
        { label: 'Last 24h', value: '+€330 · +4 inv.' },
        { label: 'Usual pace', value: '€1,100/day' },
        { label: 'Raised', value: '€43,520 · 54%' },
        { label: 'At this pace', value: '61% by Fri 13 Nov' },
      ],
      platform: 'Companisto',
      closes: '○ Closes Fri 13 Nov 2026 · 23:59 CET',
      left: '16 days',
      why: 'Why you got this: Morrow Foods is on your watchlist and you asked for momentum alarms.',
    },
  },
};

/** The scenario the page opens on. */
export const FIRST_PACE: PaceKind = 'up';

/** A picker card's value as one of ours; null for anything else. */
export function paceKind(value: unknown): PaceKind | null {
  return (PACE_KINDS as readonly unknown[]).includes(value)
    ? (value as PaceKind)
    : null;
}

/** The picker's three cards, in order. */
export const PACE_CHOICES = PACE_KINDS.map((key) => ({
  key,
  glyph: PACE_GLYPHS[key],
  title: MOMENTUM[key].title,
  description: MOMENTUM[key].description,
}));

/* ---- The chart ----------------------------------------------------------- */

/** Headroom over the tallest bar, so today's never touches the top. */
const HEADROOM = 1.22;

/** The least a bar is drawn at, so a near-empty day still shows. */
const MIN_BAR = 1.5;

/** The value at the top of the chart. */
export function chartTop(s: MomentumScenario): number {
  return Math.max(...s.bars, s.usual) * HEADROOM;
}

/** Each day's bar, in percent of the chart's height. */
export function barHeights(s: MomentumScenario): number[] {
  const top = chartTop(s);
  return s.bars.map((v) => Math.max(MIN_BAR, (v / top) * 100));
}

/** Where the usual-pace line sits, in percent of the chart's height. */
export function usualAt(s: MomentumScenario): number {
  return (s.usual / chartTop(s)) * 100;
}

/** "41.20%": a share as a CSS length, to two places. */
export function pctCss(n: number): string {
  return `${n.toFixed(2)}%`;
}

/* ---- How it's measured, and what it won't do ------------------------------- */

export const HOW_MEASURED = [
  {
    title: 'Checked all day',
    body: 'We read each platform’s progress figure every 15–60 minutes and keep every reading.',
  },
  {
    title: 'Compared with itself',
    body: 'Usual pace is the median daily amount over the raise’s previous 7 days, so a $50k raise and a $5M raise are each judged on their own terms.',
  },
  {
    title: 'One email, with the numbers',
    body: 'Last 24 hours, usual pace, amount raised and, at this pace, when it reaches its goal. Then one button that opens the platform.',
  },
];

export const WONT = [
  'No recommendations. It reports pace; what you do with it is up to you.',
  'No alarm until a raise has 7 days of history, or if its platform doesn’t publish progress often enough.',
  'No flood: one pace email per raise a day, three a day in total. The rest arrive as one bundle.',
  'Nothing during your quiet hours. Overnight changes are rechecked and sent at 07:00.',
];

/** Where "Pro adds…" goes: the Pro column on /pricing. */
export const PRO_HREF = '/pricing#tier-pro';

/** The alert section's footer for one scenario's email. */
export function momentumWhy(s: MomentumScenario): string {
  return `${s.email.why} ${WHY_END}`;
}
